import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Clock, Users } from "lucide-react";

// Counts down to a target date. Defaults to 30 days from initial render
// (locked per-browser via localStorage so the timer doesn't "reset" on reload).
const STORAGE_KEY = "shah_intake_deadline_v1";
const DAYS_AHEAD = 30;
const TOTAL_SEATS = 25;

function getDeadline() {
  if (typeof window === "undefined") return Date.now() + DAYS_AHEAD * 86400000;
  let stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const t = parseInt(stored, 10);
    if (!Number.isNaN(t) && t > Date.now()) return t;
  }
  const t = Date.now() + DAYS_AHEAD * 86400000;
  localStorage.setItem(STORAGE_KEY, String(t));
  return t;
}

function getSeatsLeft(deadline) {
  // Linear decay from TOTAL_SEATS → 3 across the countdown window.
  const total = DAYS_AHEAD * 86400000;
  const remaining = Math.max(0, deadline - Date.now());
  const ratio = remaining / total;
  return Math.max(3, Math.round(3 + (TOTAL_SEATS - 3) * ratio));
}

function pad(n) {
  return String(n).padStart(2, "0");
}

export default function UrgencyBanner() {
  const [deadline] = useState(getDeadline());
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = Math.max(0, deadline - now);
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  const seats = getSeatsLeft(deadline);

  const items = [
    { v: pad(days), l: "Days" },
    { v: pad(hours), l: "Hours" },
    { v: pad(mins), l: "Mins" },
    { v: pad(secs), l: "Secs" },
  ];

  return (
    <section className="relative bg-[#c92e34] text-white border-y-[3px] border-[#282829] overflow-hidden" data-testid="urgency-banner">
      {/* dotted noise */}
      <div aria-hidden className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{
        backgroundImage: "radial-gradient(rgb(255,255,255) 1.2px, transparent 1.2px)",
        backgroundSize: "22px 22px"
      }} />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-6 md:py-7 flex flex-col md:flex-row items-center gap-5 md:gap-8">
        {/* Headline */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <motion.div
            animate={{ rotate: [0, -8, 8, -8, 0] }}
            transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
            className="w-12 h-12 rounded-full bg-white text-[#c92e34] border-2 border-[#282829] grid place-items-center flex-shrink-0 brutal-shadow-ink"
          >
            <Flame size={22} strokeWidth={2.5} />
          </motion.div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase opacity-90">Limited Seats · Fall 2026 Intake</div>
            <div className="font-display text-xl sm:text-2xl md:text-3xl leading-tight truncate">
              Only <span className="bg-white text-[#c92e34] px-2 py-0.5 rounded-md border-2 border-[#282829] inline-flex items-center gap-1 mx-0.5">
                <Users size={16} strokeWidth={3} />
                <span data-testid="urgency-seats">{seats}</span>
              </span> counselling spots left.
            </div>
          </div>
        </div>

        {/* Countdown */}
        <div className="flex items-center gap-2 sm:gap-3" data-testid="urgency-countdown">
          <Clock size={18} className="hidden sm:block opacity-80" />
          {items.map((it, i) => (
            <div key={it.l} className="flex items-center">
              <div className="bg-[#282829] text-white border-2 border-[#282829] rounded-xl w-14 sm:w-16 py-2 text-center">
                <div className="font-anton text-2xl sm:text-3xl leading-none tabular-nums">{it.v}</div>
                <div className="text-[9px] tracking-widest uppercase opacity-70 mt-0.5">{it.l}</div>
              </div>
              {i < items.length - 1 && <span className="font-anton text-2xl mx-1 opacity-70">:</span>}
            </div>
          ))}
        </div>

        {/* CTA */}
        <a
          href="#inquiry"
          data-testid="urgency-cta"
          className="bg-white hover:bg-[#282829] hover:text-white text-[#282829] font-bold px-6 py-3 rounded-full border-2 border-[#282829] brutal-shadow-ink text-sm whitespace-nowrap transition-colors"
        >
          Claim My Spot →
        </a>
      </div>
    </section>
  );
}
